import React, { useState, useEffect, useMemo, useCallback } from "react";
import BundledEditor from "./BundleEditor";
import * as UTILS from "../../../utils/Utils";
import { useTinyStyleFormats } from "./useTinyStyleFormats";
import tinyStyles, { contentTags, emojiToUnicode, getCurrentStyles, selectionIsAList } from "../TABLE/tableUtils"
import { cloneDeep } from "lodash";

function TinyEditor(props) {
  const [processContent, setProcessContent] = useState(false);

  const colorMap = useMemo(() => {
    if (props.preset) {
      let thing = cloneDeep(props.preset);
      return thing.reduce((acc, curr, index) => {
        acc.push(curr.replace(/#\b/g, ""));
        acc.push("");
        return acc;
      }, []);
    }
    return ["ffffff", "black", "white", "00000"];
  }, [props.preset]);

  const { formats } = useTinyStyleFormats(props);


  useEffect(() => {
    dragElement(document.getElementById(props.ID));
    // 
    function dragElement(elmnt) {
      var pos1 = 0,
        pos2 = 0,
        pos3 = 0,
        pos4 = 0;
      if (document.getElementById(elmnt.id)) {
        // if present, the header is where you move the DIV from:
        document.getElementById(elmnt.id).onmousedown = dragMouseDown;
      } else {
        // otherwise, move the DIV from anywhere inside the DIV:
        elmnt.onmousedown = dragMouseDown;
      }

      function dragMouseDown(e) {
        e.preventDefault();
        e.stopPropagation();
        e = e || window.event;

        // get the mouse cursor position at startup:
        pos3 = e.clientX;
        pos4 = e.clientY;
        document.onmouseup = closeDragElement;
        // call a function whenever the cursor moves:
        document.onmousemove = elementDrag;
      }

      function elementDrag(e) {
        e.preventDefault();
        e.stopPropagation();
        e = e || window.event;

        // calculate the new cursor position:
        pos1 = pos3 - e.clientX;
        pos2 = pos4 - e.clientY;
        pos3 = e.clientX;
        pos4 = e.clientY;
        // set the element's new position:
        elmnt.style.top = elmnt.offsetTop - pos2 + "px";
        elmnt.style.left = elmnt.offsetLeft - pos1 + "px";
      }

      function closeDragElement() {
        // stop moving when mouse button is released:
        document.onmouseup = null;
        document.onmousemove = null;
      }
    }
  }, []);

  const handleFocus = useCallback(() => {
    const containerId = props.ID.replace("text-element", "");
    const container = document.getElementById(containerId);
    const toolbar = document.getElementById(props.ID);
    const containerRect = container.getBoundingClientRect();
    const toolbarHeight = 85;
    const toolbarWidth = 700; //update this if more buttons are added

    const isMobile = props.mobileView;
    const offsetY = 18;
    const offsetX = 14;
    const topLimit = isMobile ? 180 : toolbarHeight;

    toolbar.style.top = containerRect.top >= topLimit
      ? -(toolbarHeight) + 'px'
      : (container.offsetHeight + offsetY) + 'px';

    // avoid toolbar going out of the screen
    if (containerRect.left + toolbarWidth > window.innerWidth) {
      toolbar.style.left = -(containerRect.left + toolbarWidth + offsetX - window.innerWidth) + 'px';
    }
  }, [props.ID, props.mobileView]);

  const handleEditorChange = useCallback(() => {
    handleFocus();
  }, [handleFocus]);

  useEffect(() => {
    if (processContent) {
      const container = document.getElementById(props?.element?.id);
      if (!container) return;

      const listItems = container.querySelectorAll("li");
      if (listItems.length > 0) {
        listItems.forEach((li) => {
          const treeWalker = document.createTreeWalker(
            li,
            NodeFilter.SHOW_TEXT,
            {
              acceptNode: (node) => {
                const text = node.nodeValue.trim();
                return text.length > 0 ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
              }
            },
            false
          );

          let hasValidText = false;
          while (treeWalker.nextNode()) {
            hasValidText = true;
          }

          if (!hasValidText) {
            li.remove();
          }
        });


        listItems.forEach((li) => {
          const treeWalker = document.createTreeWalker(
            li,
            NodeFilter.SHOW_TEXT,
            null,
            false
          );

          let currentNode;
          while ((currentNode = treeWalker.nextNode())) {
            if (currentNode.nodeValue.includes("\uFEFF")) {
              currentNode.nodeValue = currentNode.nodeValue.replace(/\uFEFF/g, "");
            }
          }
        });
      }

      setProcessContent(false);
    }
  }, [processContent]);

  const preProcessContent = (content) => {
    let boldFlag = false;
    if (/font-weight\s*:\s*[^;]+;?\s*/gi.test(content)) {
      boldFlag = true;
      content = content.replace(/font-weight\s*:\s*[^;]+;?\s*/gi, ''); // Remove font-weight since canvas doesn't support it
    }
    content = content.replace(/\uFEFF/g, ''); // Remove BOM
    content = content.replace(/&nbsp;/g, ' '); // Replace &nbsp; with spaces

    if (boldFlag) {
      const encodedHTMLContent = emojiToUnicode(content);
      props.onEditorChange(encodedHTMLContent);
    }

    return content;
  }

  const baseFormats = ["p", "div", "h1", "h2", "h3", "h4"];
  return (
    <>
      <div className="tinyMCE-Draggable-Toolbar-Container" id={props.ID}></div>

      <BundledEditor
        initialValue={preProcessContent(props.textContent)}
        onFocus={handleFocus}
        onEditorChange={handleEditorChange}
        init={{
          placeholder: "This is text content. Edit here...",
          skin: "oxide",
          plugins: "lists link powerpaste charmap emoticons",
          promotion: false,
          toolbar: [
            " undo redo  | styles customInsertButton| fontfamily | fontsizeinput | lineheight ",
            "bold italic underline backcolor forecolor  linkPage | link  | bullist numlist outdent indent| subscript superscript charmap emoticons",
          ],
          menubar: "",
          browser_spellcheck: true,
          paste_block_drop: true,
          ID: props.ID,
          link_list: props.pagesList,
          link_default_protocol: "http",
          link_default_target: "_blank",
          inline: true,
          lineheight_formats: "1 1.1 1.2 1.25 1.3 1.4 1.5 2",
          color_map: colorMap,
          font_family_formats: UTILS.googleFontsEditor,
          forced_root_block: "div",
          forced_root_block_attrs: {
            style: `font-family:${props.selectedFont}; font-size:14px;`,
          },
          powerpaste_allow_local_images: true,
          powerpaste_word_import: "prompt",
          powerpaste_html_import: "prompt",
          selector: "#" + props.ID,
          fixed_toolbar_container: "#" + props.ID,
          toolbar_sticky: true,
          formats: formats,
          style_formats: tinyStyles.style_formats,
          relative_urls: false,
          remove_script_host: false,
          setup: (editor) => {
            const primaryFormats = ['h1', 'h2', 'h3', 'h4', 'p'];

            let currentBaseFormat = null;
            let currentFormats = null;
            let currentTags = null;
            editor.on('BeforeSetMode SetMode', (e) =>
              console.log('Mode:', editor.mode.get(), e.name)
            );
            editor.on('BeforeExecCommand', function (e) {
              const contentBefore = editor.selection.getContent({ format: 'html' });
              currentFormats = getCurrentStyles(editor);
              currentBaseFormat = currentFormats.find(format => primaryFormats.includes(format));
              currentTags = contentTags(contentBefore);
            });
            editor.on('NodeChange', (e) => {
            });
            editor.on('FormatApply', (e) => {
            });
            editor.on('ExecCommand', (e) => {
              // STYLE FORMAT COMMANDS`
              const selectedContent = editor.selection.getContent({ format: 'html' });
              const selectedNode = editor.selection.getNode();
              const currentFormats = getCurrentStyles(editor);
              const isInList = selectedContent.includes('<ul>') ||
                selectedContent.includes('<ol>') ||
                selectedContent.includes('<li>') ||
                editor.selection.getNode().closest('ul, ol, li') !== null;


              switch (e.command) {
                case "mceToggleFormat":
                  // formats
                  if (baseFormats.includes(e.value)) {
                    const formatName = e.value;
                    editor.execCommand("RemoveFormat");
                    editor.formatter.apply(formatName);
                    if (selectedContent.includes('<ul>') || selectedContent.includes('<ol>')) {
                      setProcessContent(true);
                      editor.formatter.apply(formatName + "_list");
                      e.preventDefault();
                    } else {
                      editor.formatter.apply(formatName);
                      e.preventDefault();
                    }
                  }
                  switch (e.value) {
                    case "underline":
                      // Clean text-decoration: underline from anchor tags using TinyMCE's DOM methods
                      const selectedNode = editor.selection.getNode();
                      const anchorElements = selectedNode.querySelectorAll('a');

                      if (anchorElements.length > 0) {
                        anchorElements.forEach(anchor => {
                          const currentStyle = anchor.getAttribute('style') || '';
                          const updatedStyle = currentStyle.replace(/text-decoration\s*:\s*underline\s*;?\s*/gi, '').trim();

                          if (updatedStyle) {
                            anchor.setAttribute('style', updatedStyle);
                            anchor.setAttribute('data-mce-style', updatedStyle);
                          } else {
                            anchor.removeAttribute('style');
                            anchor.removeAttribute('data-mce-style');
                          }
                        });
                      }
                      break;
                  }
                  // LOGIC FOR LIST ITEMS WHEN FORMAT IS APPLIED
                  if (isInList) {
                    const textHasBaseFormat = currentFormats.some(format => baseFormats.includes(format));
                    if (textHasBaseFormat) {
                      const node = editor.selection.getNode();
                      const liNode = node.querySelector('li');
                      switch (e.value) {
                        case "bold":
                          liNode.style.fontWeight = "bold";
                          break;
                        case "italic":
                          liNode.style.fontStyle = "italic";
                          break;
                      }
                    }
                  }
                  break;

                case "mceApplyTextcolor":
                  const liParent = editor.dom.getParent(selectedNode, 'li');
                  if (e.ui === "forecolor") {
                    const color = e.value;

                    // CASE WHEN 2 LI ITEMS ARE SELECTED OR THE WHOLE LIST IS SELECTED
                    if (selectedContent.includes('<ul>') || selectedContent.includes('<ol>')) {
                      const tempDiv = editor.dom.create('div');
                      tempDiv.innerHTML = selectedContent;
                      const selectedLiElements = tempDiv.querySelectorAll('li');

                      // Get the text content of the selected li elements
                      const selectedInnerTexts = new Set();
                      selectedLiElements.forEach(li => {
                        const text = li.textContent.trim();
                        if (text) {
                          selectedInnerTexts.add(text);
                        }
                      });

                      const allLiElements = selectedNode.querySelectorAll('li');
                      allLiElements.forEach(li => {
                        const text = li.textContent.trim();
                        // Apply color to each selected li element
                        if (selectedInnerTexts.has(text)) {
                          const existingStyle = li.getAttribute('style') || '';
                          const cleanedStyle = existingStyle.replace(/color\s*:\s*[^;]+;?/i, '').trim();
                          const newStyle = `${cleanedStyle} color: ${color};`.trim();
                          li.setAttribute('style', newStyle);
                          li.setAttribute('data-mce-style', newStyle);
                        }
                      });
                    }

                    // CASE WHEN TEXT OF A LIST ITEM IS SELECTED
                    if (liParent) {

                      const liParentText = liParent.innerText;
                      const selectedText = editor.selection.getContent({ format: 'text' });

                      // IF THE SELECTED TEXT IS THE SAME AS THE LI PARENT TEXT, APPLY THE COLOR TO THE LI PARENT
                      if (liParentText === selectedText) {
                        const existingStyle = liParent.getAttribute('style') || '';
                        const cleanedStyle = existingStyle.replace(/color\s*:\s*[^;]+;?/i, '').trim();
                        const newStyle = `${cleanedStyle} color: ${color};`.trim();
                        liParent.setAttribute('style', newStyle);
                        liParent.setAttribute('data-mce-style', newStyle);
                      }

                    }
                  }
                  break;

                case "FontSize":
                  if (selectedContent.includes('<ul>') || selectedContent.includes('<ol>')) {
                    const node = editor.selection.getNode();
                    const liNode = node.querySelector('li');
                    if (liNode) {
                      const existingStyle = liNode.getAttribute('style') || '';
                      const cleanedStyle = existingStyle.replace(/font-size\s*:\s*[^;]+;?/i, '').trim();
                      const newStyle = `${cleanedStyle} font-size: ${e.value};`.trim();
                      liNode.setAttribute('style', newStyle);
                      liNode.setAttribute('data-mce-style', newStyle);
                    }
                  }
                  break;

                case "FontName":
                  if (selectedContent.includes('<ul>') || selectedContent.includes('<ol>')) {
                    const node = editor.selection.getNode();
                    const liNode = node.querySelector('li');
                    if (liNode) {
                      const existingStyle = liNode.getAttribute('style') || '';
                      const cleanedStyle = existingStyle.replace(/font-family\s*:\s*[^;]+;?/i, '').trim();
                      const newStyle = `${cleanedStyle} font-family: ${e.value};`.trim();
                      liNode.setAttribute('style', newStyle);
                      liNode.setAttribute('data-mce-style', newStyle);
                    }
                  }
                  break;

                case "InsertUnorderedList":
                case "InsertOrderedList":
                  if (selectedContent.includes('<ul>') || selectedContent.includes('<ol>')) {
                    const formatsToPreserve = currentBaseFormat;
                    if (formatsToPreserve) {
                      editor.formatter.apply(formatsToPreserve + "_list");
                      editor.formatter.apply(formatsToPreserve);
                    }
                    const node = editor.selection.getNode();
                    const list = node.querySelectorAll('li');
                    list.forEach((li) => {
                      if (!li.innerText.trim()) {
                        li.remove();
                      }
                    });
                  }
                  break;

                case "SelectAll":
                  if (e.target) {
                    const allSelectedContent = e.target.selection.getContent();
                    const tags = contentTags(allSelectedContent);
                    if (selectionIsAList(allSelectedContent)) { //is a list
                      if (tags?.length === 1) {
                        // apply format to display correct format 
                        const formatsToPreserve = tags[0];
                        editor.formatter.apply(formatsToPreserve);
                      }
                    } else { //not a list
                    }
                  }
                  break;
              }

            });
            editor.on('init', function () {
              document.addEventListener('touchmove', function (event) { }, { passive: true });
              // If not using iframe, access the root element (contenteditable mode)
              const editorBody = editor.getBody(); // This is the contenteditable div
              editorBody.addEventListener(
                "touchstart",
                function (e) {
                  // Your custom code here
                },
                { passive: true }
              );

              editorBody.addEventListener(
                "touchmove",
                function (e) {
                  // Your custom code here
                },
                { passive: true }
              );

              editorBody.addEventListener(
                "wheel",
                function (e) {
                  // Your custom code here
                },
                { passive: true }
              );
            });

            editor.ui.registry.addButton("customInsertButton", {
              tooltip: "Clear Formatting",
              icon: "remove-formatting",
              onAction: function (e) {
                const beforeText = editor.getContent({ format: 'text' });

                if (editor.selection.getContent() === '') {
                  editor.execCommand('SelectAll');
                }

                const contentToClean = editor.selection.getContent();
                const nodeToClean = editor.selection.getNode();

                // ANCHOR TAGS CLEANING
                // Remove all anchor tags from the selected content
                const anchorElements = editor.dom.select('a', nodeToClean);
                if (anchorElements.length > 0) {
                  // If we have anchor elements, remove them while preserving their content
                  anchorElements.forEach(anchor => {
                    // Move all child nodes out of the anchor before removing it
                    while (anchor.firstChild) {
                      anchor.parentNode.insertBefore(anchor.firstChild, anchor);
                    }
                    // Remove the empty anchor element
                    editor.dom.remove(anchor);
                  });
                }
                // ANCHOR TAGS CLEANING

                // LIST CLEANING
                if (contentToClean.includes('<li>') || contentToClean.includes('<ul>') || contentToClean.includes('<ol>')) {
                  const parent = editor.dom.getParent(nodeToClean);
                  if (parent && parent.tagName === 'DIV' && !parent.classList.contains('mce-content-body')) {
                    editor.dom.remove(parent, true);
                    setTimeout(() => {
                      editor.execCommand('RemoveList');
                      editor.formatter.apply("none");
                    }, 0);
                  } else {
                    editor.execCommand('RemoveList');
                  }
                }
                // LIST CLEANING

                // APPLY DEFAULT FORMAT & REMOVE FORMAT
                // Always apply "none" format regardless of current state
                editor.formatter.apply("none");
                // double is needed to remove the format from the list items then inline
                editor.execCommand("RemoveFormat");
                editor.execCommand("RemoveFormat");
                // APPLY DEFAULT FORMAT & REMOVE FORMAT

                setTimeout(() => {
                  const content = editor.getContent();

                  const parser = new DOMParser();
                  const doc = parser.parseFromString(content, 'text/html');

                  const bodyChildren = doc.body.children;

                  // REMOVE EMPTY SPACES
                  if (bodyChildren.length > 0) {
                    if (bodyChildren[0].innerHTML.trim() === '&nbsp;') {
                      bodyChildren[0].remove();
                    }
                  }

                  if (bodyChildren.length > 1) {
                    const lastChild = bodyChildren[bodyChildren.length - 1];
                    if (lastChild.innerHTML.trim() === '&nbsp;') {
                      lastChild.remove();
                    }
                  }
                  // REMOVE EMPTY SPACES

                  // SET CONTENT
                  editor.setContent(doc.body.innerHTML);
                  editor.execCommand("SelectAll");
                }, 0);
              },
            });

            // editor.on("focus",);
            editor.on("OpenWindow", () => {
              document.getElementById(props.ID).onmousedown = null;
              document.getElementById(props.ID).onmouseup = null;
              document.getElementById(props.ID).onmousemove = null;
            });
            editor.on("CloseWindow", () => {
              dragElement(document.getElementById(props.ID));

              function dragElement(elmnt) {
                var pos1 = 0,
                  pos2 = 0,
                  pos3 = 0,
                  pos4 = 0;
                if (document.getElementById(elmnt.id)) {
                  // if present, the header is where you move the DIV from:
                  document.getElementById(elmnt.id).onmousedown = dragMouseDown;
                } else {
                  // otherwise, move the DIV from anywhere inside the DIV:
                  elmnt.onmousedown = dragMouseDown;
                }

                function dragMouseDown(e) {
                  e.preventDefault();
                  e.stopPropagation();
                  e = e || window.event;

                  // get the mouse cursor position at startup:
                  pos3 = e.clientX;
                  pos4 = e.clientY;
                  document.onmouseup = closeDragElement;
                  // call a function whenever the cursor moves:
                  document.onmousemove = elementDrag;
                }

                function elementDrag(e) {
                  e.preventDefault();
                  e.stopPropagation();
                  e = e || window.event;

                  // calculate the new cursor position:
                  pos1 = pos3 - e.clientX;
                  pos2 = pos4 - e.clientY;
                  pos3 = e.clientX;
                  pos4 = e.clientY;
                  // set the element's new position:
                  elmnt.style.top = elmnt.offsetTop - pos2 + "px";
                  elmnt.style.left = elmnt.offsetLeft - pos1 + "px";
                }

                function closeDragElement() {
                  // stop moving when mouse button is released:
                  document.onmouseup = null;
                  document.onmousemove = null;
                }
              }
            });
            editor.on("OpenWindow", () => {
              // Use a Map to store removed classes with elements as keys
              let removedClassesMap = new Map();

              function removeDragHandleClasses() {
                const elements = document.querySelectorAll(
                  '[class*="dragHandle"]'
                );
                elements.forEach((element) => {
                  // Store removed classes for this element
                  const removedClasses = [];
                  element.classList.forEach((className) => {
                    if (className.includes("dragHandle")) {
                      removedClasses.push(className);
                      element.classList.remove(className);
                    }
                  });
                  // Store the removed classes in the map
                  if (removedClasses.length > 0) {
                    removedClassesMap.set(element, removedClasses);
                  }
                });
              }

              // Remove drag handle classes on mount
              removeDragHandleClasses();

              // Restore drag handle classes on cleanup
              editor.on("CloseWindow", () => {
                function restoreDragHandleClasses() {
                  removedClassesMap.forEach((removedClasses, element) => {
                    removedClasses.forEach((className) => {
                      element.classList.add(className);
                    });
                  });
                  // Clear the map after restoring
                  removedClassesMap.clear();
                }
                restoreDragHandleClasses();
              });
            });
            editor.on("blur", () => {
              let content = editor.getContent();

              /**
               * Converts emojis to HTML numeric character references (NCR)
               * This ensures emojis are properly encoded for database storage
               * and prevents parsing issues with hexadecimal characters (b,c,d,e,f)
               * 
               * Version 2: Fixed issues with missing semicolons and improved emoji detection
               */
              if (props.textContent !== content) {
                content = content.replace(/font-weight\s*:\s*[^;]+;?\s*/gi, '');
                const encodedHTMLContent = emojiToUnicode(content);
                props.onEditorChange(encodedHTMLContent);
              }

              document.getElementById(props.ID).style.top = "-100px";
              document.getElementById(props.ID).style.left = "-10px";
            });
          },
        }}
      />
      {/* <button onClick={log}>Log editor content</button> */}
    </>
  );
}

export default React.memo(TinyEditor);
